import React from 'react';
import Cta from '../components/Cta';
import Reveal from '../components/Reveal';

const founders = [
  {
    name: 'Saziso Mpofu',
    image: '/img/about/1.png',
    bio: "Saziso Mpofu's journey into caregiving began when she took care of Lillian (Lilly) in her final days. This profound experience transformed her understanding of compassionate care and the dignity every person deserves in their vulnerable moments. The deep bond formed during this time inspired Saziso to ensure that Lilly's legacy of kindness and care would continue to touch others' lives.",
  },
  {
    name: 'Sikhanyiswe Mpofu',
    image: '/img/about/2.png',
    bio: 'Similarly, Sikhanyiswe Mpofu dedicated herself to caring for Leo in his last days. This experience sparked a passion for elderly care and revealed the critical need for properly trained caregivers. The challenges and rewards of this intimate caregiving journey became the foundation for what would later become Leolilian Care International.',
  },
];

const chapters = [
  {
    label: 'Our Vision',
    body: 'Our vision extends beyond just training caregivers. We are working towards establishing a home-based care institution, where families can access personalized caregiving services tailored to their specific needs. This institution will offer full support to both families and professional caregivers, ensuring that loved ones receive compassionate and professional care at home.',
  },
  {
    label: 'Our Mission',
    body: 'Our mission is to empower caregivers with the knowledge and skills they need to provide dignified and effective care. Leolilian Care International aims to be a beacon of hope, creating a community where caregivers can thrive, and families can feel confident in the care their loved ones receive.',
  },
  {
    label: 'Looking Ahead',
    body: 'Looking ahead, we envision a world where caregiving is not just a profession, but a way to foster love, respect, and community. As we continue to grow, our focus will be on expanding our educational programs and building a network of care that spans across different regions, ensuring that the legacy of Leo and Lilly continues to touch lives worldwide.',
  },
];

const AboutPage: React.FC = () => {
  return (
    <section className="about-page min-h-[100dvh] bg-mesh-warm px-4 pb-32 pt-36 sm:px-8 sm:pt-44 lg:px-12">
      <div className="mx-auto max-w-7xl">
        {/* Editorial Split intro */}
        <div className="grid gap-10 lg:grid-cols-12 lg:gap-8">
          <div className="lg:col-span-7">
            <Reveal>
              <span className="eyebrow bg-plum-pale text-plum-deep">Our story</span>
              <h1 className="mt-7 font-display text-[3rem] font-light leading-[0.96] tracking-[-0.035em] text-espresso-900 sm:text-7xl">
                About Leolilian
                <br />
                <span className="italic text-sage-deep">Care</span> International
              </h1>
            </Reveal>
          </div>
          <Reveal delay={120} className="lg:col-span-5">
            <p className="text-[15px] leading-relaxed text-espresso-600 lg:mt-6">
              Founded in 2024 by the children of Leo and Lilly, Leolilian Care International
              was created to continue their legacy of caregiving. We are committed to
              providing accessible, flexible and career-focused foundation training, and now
              offer three specialised online programmes — Caregiver, Cruise Ship &amp;
              Hospitality, and Professional Chef.
            </p>
          </Reveal>
        </div>

        {/* Founders */}
        <div className="mt-24 border-t border-espresso-700/10 pt-16">
          <Reveal>
            <h2 className="font-display text-4xl font-light tracking-tight text-espresso-900 sm:text-5xl">
              The daughters behind the name
            </h2>
          </Reveal>

          <div className="mt-14 grid gap-6 lg:grid-cols-2">
            {founders.map((founder, i) => (
              <Reveal key={founder.name} delay={i * 120}>
                <article className="bezel-shell-lg group h-full transition-all duration-700 ease-fluid hover:-translate-y-1 hover:shadow-lifted">
                  <div className="bezel-core-lg flex h-full flex-col overflow-hidden">
                    {/* These are cut-out portraits on a white ground, so they are
                        contained on a tinted panel rather than cover-cropped —
                        cropping leaves the subject small and floating. */}
                    <div className="overflow-hidden rounded-[calc(2.5rem-0.5rem)] rounded-b-none bg-gradient-to-b from-sage-pale/50 to-cream-100">
                      <img
                        src={founder.image}
                        alt={founder.name}
                        loading="lazy"
                        className="mx-auto h-80 w-auto max-w-full object-contain object-bottom mix-blend-multiply transition-transform duration-1200 ease-fluid group-hover:scale-[1.04]"
                      />
                    </div>
                    <div className="flex flex-1 flex-col p-8 sm:p-10">
                      <h3 className="font-display text-3xl font-light tracking-tight text-espresso-900">
                        {founder.name}
                      </h3>
                      <p className="mt-5 flex-1 text-[15px] leading-relaxed text-espresso-600">
                        {founder.bio}
                      </p>
                    </div>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>

          <Reveal delay={140}>
            <p className="mx-auto mt-16 max-w-3xl text-center font-display text-2xl font-light italic leading-relaxed text-espresso-700 sm:text-3xl">
              “From their personal experiences caring for their parents, Saziso and
              Sikhanyiswe gained invaluable insights into the art and science of caregiving —
              a living legacy that combines their parents' names and embodies their spirit of
              love and service.”
            </p>
          </Reveal>
        </div>

        {/* Vision / Mission / Looking Ahead — asymmetric stack */}
        <div className="mt-28 grid grid-cols-1 gap-4 md:grid-cols-12">
          {chapters.map((chapter, i) => (
            <Reveal
              key={chapter.label}
              delay={i * 90}
              className={i === 0 ? 'md:col-span-12' : 'md:col-span-6'}
            >
              <div className="bezel-shell h-full">
                <div className="bezel-core h-full p-8 sm:p-10">
                  <span className="eyebrow bg-sage-pale text-sage-deep">{chapter.label}</span>
                  <p className="mt-6 text-[15px] leading-relaxed text-espresso-600">
                    {chapter.body}
                  </p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={120}>
          <div className="bezel-shell-lg mt-6">
            <div className="bezel-core-lg flex flex-col items-center justify-between gap-8 bg-espresso-800 px-8 py-14 text-cream sm:flex-row sm:px-14">
              <h2 className="max-w-lg font-display text-3xl font-light leading-tight tracking-tight sm:text-4xl">
                Build your career on the same foundation.
              </h2>
              <Cta to="/enroll" variant="light">
                Enroll now
              </Cta>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default AboutPage;
